/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { useSelector, useDispatch } from 'react-redux';

import SimpleMap from 'shared-ui/maps/templates/SimpleMap';
import { BOUNDARY_TYPES } from 'shared-ui/maps/constants';
import { convertSearchResultToGeojson } from 'shared-ui/maps/helpers/parseFeatures';
import { featureCollection, turfFeature } from 'shared-ui/maps/turfUtils';

import * as searchActions from 'ui-search/src/actions/search';

import { fetchMapInventory as fetchMapInventoryAction } from '../redux/map';

function MapContainer(props = {}) {
    const dispatch = useDispatch();
    const fetchMapInventory = (params) => dispatch(fetchMapInventoryAction(params));
    const fetchBoundarySearchResults = (type, name) =>
        dispatch(searchActions.fetchBoundary(type, name));

    const latitude = useSelector((state) =>
        get(state, 'map.mapSearchResultLocation.latitude', null)
    );
    const longitude = useSelector((state) =>
        get(state, 'map.mapSearchResultLocation.longitude', null)
    );
    const zoomLevel = useSelector((state) =>
        get(state, 'map.mapSearchResultLocation.zoomLevel', null)
    );
    const mapInventory = useSelector((state) => get(state, 'map.mapInventory', []));
    const facilityName = useSelector((state) =>
        get(state, 'gateway.gateway.results.recarea_name', null)
    );
    const boundaryResults = useSelector((state) =>
        get(state, 'searchResults.boundaryResults', [])
    );

    const [inventoryFacilityName, setInventoryFacilityName] = useState(
        props.gateway?.name
    );

    const mapRef = useRef();

    const getCurrentZoom = () => {
        if (mapRef.current) return mapRef.current.getZoom();
        return null;
    };

    const specialMapControls = useRef();
    const config = useRef({
        cluster: false,
        shouldAlwaysFitData: false,
        closePopupOnMove: true,
        mapSpecialControls: {
            legendContent: <ul />,
            maxZoom: 9,
            getCurrentZoom,
        },
        mapBasicControls: {
            hasReservableLocations: true,
        },
    });

    const [geojson, setGeojson] = useState(featureCollection([]));
    const [parsedInventory, setParsedInventory] = useState(featureCollection([]));
    const [center, setCenter] = useState();
    const [zoom, setZoom] = useState(4);
    const [boundaryData, setBoundaryData] = useState(null);
    const [selectedFeatureId, setSelectedFeatureId] = useState(null);

    useEffect(
        () => {
            if (!props.gateway?.name) return;
            if (!isEqual(inventoryFacilityName, props.gateway.name)) {
                setInventoryFacilityName(props.gateway.name);
            }
        },
        // Old code so declaring lint bankruptcy
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.gateway]
    );

    useEffect(
        () => {
            if (boundaryData && !boundaryResults?.length) {
                setBoundaryData(null);
                return;
            }

            const boundaryResult = boundaryResults[0];
            const boundaryGeojson = boundaryResult?.geo_json;
            if (!boundaryGeojson) return;
            const boundaryFeature = turfFeature(boundaryGeojson, {
                ...boundaryResult,
                geo_json: null,
                type: BOUNDARY_TYPES.facility,
            });
            if (!isEqual(boundaryData, boundaryFeature)) setBoundaryData(boundaryFeature);
        },
        // Old code so declaring lint bankruptcy
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [boundaryResults]
    );

    useEffect(
        () => {
            if (!latitude || !longitude) return;
            const coords = [longitude, latitude];
            if (!isEqual(coords, center)) setCenter(coords);
        },
        // Old code so declaring lint bankruptcy
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [latitude, longitude]
    );

    useEffect(
        () => {
            if (zoomLevel && !isEqual(zoomLevel, zoom)) setZoom(zoomLevel);
        },
        // Old code so declaring lint bankruptcy
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [zoomLevel]
    );

    const getInventoryData = (map) => {
        if (!map) return;
        const mapBounds = map.getBounds();
        const geoSearchArgs = {
            lat_sw: mapBounds._sw.lat,
            lng_sw: mapBounds._sw.lng,
            lat_ne: mapBounds._ne.lat,
            lng_ne: mapBounds._ne.lng,
        };

        fetchMapInventory(geoSearchArgs);

        specialMapControls.current.legend?.updateGlobalContent({
            maxZoom: 9,
            currentZoom: getCurrentZoom(),
        });
    };

    const debounceGetInventoryData = debounce(getInventoryData, 500);

    useEffect(
        () => {
            const updated = convertSearchResultToGeojson(mapInventory);
            if (!isEqual(updated, parsedInventory)) setParsedInventory(updated);
            if (!isEqual(updated, geojson)) {
                setGeojson(updated);
                const nameParam = facilityName || inventoryFacilityName;
                if (nameParam) {
                    fetchBoundarySearchResults(BOUNDARY_TYPES.facility, nameParam);
                }
            }
        },
        // Old code so declaring lint bankruptcy
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mapInventory]
    );

    const updateDataIcon = (featureId) => {
        if (!featureId) return parsedInventory;
        const updatedData = geojson?.features?.map((feature) => {
            const hasMatched =
                feature.properties.featureId?.split('_').pop() ===
                featureId?.split('_').pop();
            /* eslint-disable no-param-reassign */
            feature.properties.suffix = hasMatched ? '_active' : '';
            /* eslint-enable no-param-reassign */
            return feature;
        });
        return featureCollection(updatedData);
    };

    const updateGeojson = (featureId) => {
        const updatedData = updateDataIcon(featureId);
        if (!isEqual(updatedData, geojson)) setGeojson(updatedData);
    };

    const onSelectedEvent = (featureId) => {
        if (!isEqual(featureId, selectedFeatureId)) setSelectedFeatureId(featureId);
        updateGeojson(featureId);
    };

    return (
        <div id="gatewayMap" className="map">
            <SimpleMap
                featureName={inventoryFacilityName}
                geojson={geojson}
                boundary={boundaryData}
                config={config.current}
                mapContainer="gatewayMap"
                center={center}
                zoom={zoom}
                service="gateway"
                selectedEvent={onSelectedEvent}
                mapRef={mapRef}
                specialMapControls={specialMapControls}
                initialEvent={debounceGetInventoryData}
                onMoveEnd={debounceGetInventoryData}
            />
        </div>
    );
}

export default MapContainer;

MapContainer.propTypes = {
    gateway: PropTypes.object,
};

// cSpell:ignore recarea
